import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ujmsCaseDiscovery, ujmsProduceDiscovery, ujmsAcknowledgeDiscovery } from "../../services/ujmsApi";
import { Panel, Badge, inputCls, selectCls, Field, btnPrimary, btnGhost, fmtDT, can, EmptyState } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { FolderOpen, AlertTriangle, LockKeyhole, Plus, Trash2 } from "lucide-react";

const CATEGORIES = ["POLICE_REPORTS", "WITNESS_LIST", "EXPERT_DISCLOSURE", "MEDIA", "FORENSIC", "EXCULPATORY_BRADY", "STATEMENTS", "OTHER"];

export const DiscoveryTab = ({ caseId, access }) => {
  const [rows, setRows] = useState(null);
  const [producing, setProducing] = useState(false);
  const load = useCallback(() => {
    ujmsCaseDiscovery(caseId).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const acknowledge = async (p) => {
    try {
      await ujmsAcknowledgeDiscovery(p.id);
      toast.success("DISCOVERY_RECEIPT — acknowledged and docketed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!rows) return <Panel testId="tab-discovery"><div className="text-sm text-slate-400 p-4">Loading discovery…</div></Panel>;
  return (
    <Panel title="Discovery exchange" testId="tab-discovery"
      action={can(access, "discovery.produce") && (
        <button className={btnPrimary} onClick={() => setProducing(true)} data-testid="disc-produce-btn">New Production</button>)}>
      {rows.length === 0 ? (
        <EmptyState icon={FolderOpen} title="No discovery productions" note="Productions tendered between the State and the defense are tracked and docketed here." testId="disc-empty" />
      ) : (
        <div className="divide-y divide-slate-200">
          {rows.map((p) => (
            <div key={p.id} className="px-4 py-3" data-testid={`disc-production-${p.production_number}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-slate-900">Production #{p.production_number}</span>
                <Badge value={p.direction} />
                <Badge value={p.status} testId={`disc-status-${p.production_number}`} />
                {p.brady_material && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-amber-50 border border-amber-300 text-amber-800 rounded-sm px-1.5 py-0.5" data-testid={`disc-brady-${p.production_number}`}>
                    <AlertTriangle className="w-3 h-3" /> Brady material
                  </span>
                )}
                {p.protective_order && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-red-50 border border-red-200 text-red-700 rounded-sm px-1.5 py-0.5" data-testid={`disc-po-${p.production_number}`}>
                    <LockKeyhole className="w-3 h-3" /> Protective order
                  </span>
                )}
                <span className="text-[11px] text-slate-400 font-mono ml-auto">{fmtDT(p.created_at)} · {p.produced_by}</span>
              </div>
              <ul className="mt-2 space-y-1">
                {(p.items || []).map((it, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
                    <Badge value={it.category} /> {it.description}
                  </li>
                ))}
              </ul>
              {p.protective_order && p.protective_terms && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2.5 py-1.5 mt-2">Protective terms: {p.protective_terms}</div>
              )}
              {p.notes && <div className="text-xs text-slate-500 mt-1.5">{p.notes}</div>}
              <div className="mt-2 flex items-center gap-3">
                {p.status === "PRODUCED" && can(access, "discovery.produce") && (
                  <button className={btnGhost} onClick={() => acknowledge(p)} data-testid={`disc-ack-${p.production_number}`}>Acknowledge Receipt</button>
                )}
                {p.acknowledged_by && (
                  <span className="text-[11px] text-emerald-700">Receipt acknowledged by {p.acknowledged_by} · {fmtDT(p.acknowledged_at)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <ProduceDialog open={producing} caseId={caseId} onClose={() => setProducing(false)} onDone={load} />
    </Panel>
  );
};

function ProduceDialog({ open, caseId, onClose, onDone }) {
  const blank = { items: [{ _k: "i0", description: "", category: "POLICE_REPORTS" }], protective_order: false, protective_terms: "", notes: "" };
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) setF(blank); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const setItem = (i, k, v) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  const submit = async () => {
    try {
      await ujmsProduceDiscovery(caseId, { ...f, items: f.items.map(({ _k, ...it }) => it) });
      toast.success("DISCOVERY_PRODUCED — docketed and case team notified");
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const valid = f.items.length > 0 && f.items.every((it) => it.description.trim()) && (!f.protective_order || f.protective_terms.trim());
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="disc-produce-dialog">
        <DialogHeader><DialogTitle>New discovery production</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-[11px] text-slate-400">Direction is determined by your role (State ↔ Defense). The production is docketed and the receiving side is notified.</div>
          {f.items.map((it, i) => (
            <div key={it._k || i} className="flex gap-2 items-start">
              <select className={`${selectCls} w-44`} value={it.category} onChange={(e) => setItem(i, "category", e.target.value)} data-testid={`disc-item-category-${i}`}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input className={inputCls} value={it.description} onChange={(e) => setItem(i, "description", e.target.value)}
                placeholder="Item description…" data-testid={`disc-item-description-${i}`} />
              {f.items.length > 1 && (
                <button className="p-1.5 text-slate-400 hover:text-red-700" onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} data-testid={`disc-item-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
          <button className={btnGhost} onClick={() => setF({ ...f, items: [...f.items, { _k: Math.random().toString(36).slice(2), description: "", category: "OTHER" }] })} data-testid="disc-add-item">
            <Plus className="w-3.5 h-3.5" /> Add item
          </button>
          <label className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm px-2.5 py-1.5 cursor-pointer hover:bg-slate-50">
            <input type="checkbox" checked={f.protective_order} onChange={(e) => setF({ ...f, protective_order: e.target.checked })} data-testid="disc-po-checkbox" />
            Subject to protective order
          </label>
          {f.protective_order && (
            <Field label="Protective terms" required>
              <input className={inputCls} value={f.protective_terms} onChange={(e) => setF({ ...f, protective_terms: e.target.value })}
                placeholder="Counsel-only; no copies to be provided to defendant…" data-testid="disc-po-terms" />
            </Field>
          )}
          <Field label="Notes"><input className={inputCls} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="disc-notes" /></Field>
        </div>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnPrimary} onClick={submit} disabled={!valid} data-testid="disc-produce-submit">Tender Production</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
